import { useEffect, useMemo, useRef, useState } from 'react';
import { searchContext, userAppContext } from '../../../context/Context';
import { appendAnalyticsPages, fetchAnalyticsPage } from '../../../utils/analyticsPagination';
import buildResultEndpoint from '../../../utils/buildResultEndpoint';
import type { GtpHeatmapGeneRow } from '@/utils/buildGtpHeatmapData';

const GRAPH_PAGE_SIZE = 250;
const FETCH_DEBOUNCE_MS = 250;

type UseGenesGtpHeatmapDataParams = {
  source: string;
  view: string;
  names?: Array<{ id: string }>;
};

export function useGenesGtpHeatmapData({ source, view, names }: UseGenesGtpHeatmapDataParams) {
  const [data, setData] = useState<GtpHeatmapGeneRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { authorization, setIsAuthenticated, restrictGridResultCount } = userAppContext() || {};
  const { tab: activeTab, searchItems: contextSearchItems } = searchContext() || {
    tab: 'GENES',
    searchItems: [],
  };

  const effectiveSearchItems = names?.length ? names : contextSearchItems;
  const searchItemsKey = effectiveSearchItems?.map(item => String(item?.id ?? '')).join(',') ?? '';
  const isPanelActive = String(activeTab ?? '').toUpperCase() === 'GENES';
  const resultLimit = typeof restrictGridResultCount === 'number' ? restrictGridResultCount : null;
  const pageSize = resultLimit ?? GRAPH_PAGE_SIZE;

  const fetchTriggerKey = useMemo(
    () =>
      JSON.stringify({
        source,
        view,
        searchItemsKey,
        pageSize,
        resultLimit,
      }),
    [pageSize, resultLimit, searchItemsKey, source, view],
  );

  const paramsRef = useRef({
    view,
    source,
    effectiveSearchItems,
    pageSize,
    resultLimit,
    authorization,
    setIsAuthenticated,
    isPanelActive,
  });

  paramsRef.current = {
    view,
    source,
    effectiveSearchItems,
    pageSize,
    resultLimit,
    authorization,
    setIsAuthenticated,
    isPanelActive,
  };

  useEffect(() => {
    const p = paramsRef.current;

    if (!p.isPanelActive) {
      requestIdRef.current += 1;
      setData([]);
      setIsEmpty(false);
      setIsLoading(false);
      setError(null);
      return;
    }

    if (!searchItemsKey) {
      setData([]);
      setIsEmpty(true);
      setIsLoading(false);
      setError(null);
      return;
    }

    const requestId = ++requestIdRef.current;
    let cancelled = false;

    setData([]);
    setIsEmpty(false);
    setIsLoading(true);
    setError(null);

    const loadData = async () => {
      try {
        const buildPageRequest = (pageNum: number) => {
          const built = buildResultEndpoint({
            view: p.view,
            source: p.source,
            tab: 'GENES',
            searchItems: p.effectiveSearchItems,
            isEvidence: false,
            isMapping: false,
            mappingContent: null,
            pageNum,
            pageSize: p.pageSize,
            orderByParam: 'symbol',
            order: 'asc',
            defaultFilterValue: [],
            sourceWeights: [],
          });

          if (!built?.url) {
            throw new Error('Could not build gene heatmap request');
          }

          return {
            url: built.url,
            method: (built.method ?? 'GET') as 'GET' | 'POST',
            body: (built.body as Record<string, string | number> | null) ?? null,
          };
        };

        const firstPage = await fetchAnalyticsPage({
          request: buildPageRequest(0),
          authorization: p.authorization,
          setIsAuthenticated: p.setIsAuthenticated,
        });

        if (cancelled || requestId !== requestIdRef.current) return;

        const firstData = firstPage.rows as GtpHeatmapGeneRow[];
        if (!firstData.length) {
          setData([]);
          setIsEmpty(true);
          return;
        }

        let completeData = firstData;
        if (!p.resultLimit) {
          const totalElements = Number(
            firstPage.filteredTotal != null ? firstPage.filteredTotal : firstPage.rawTotal,
          );
          completeData = (await appendAnalyticsPages({
            isSummaryMatchPost: false,
            pageSize: p.pageSize,
            firstPageRows: firstData,
            fetchPage: pageNum =>
              fetchAnalyticsPage({
                request: buildPageRequest(pageNum),
                authorization: p.authorization,
                setIsAuthenticated: p.setIsAuthenticated,
              }),
            getTotalElements: totalElements,
          })) as GtpHeatmapGeneRow[];
        } else {
          completeData = firstData.slice(0, p.resultLimit);
        }

        if (cancelled || requestId !== requestIdRef.current) return;

        setData(completeData);
        setIsEmpty(false);
      } catch (fetchError) {
        if (cancelled || requestId !== requestIdRef.current) return;
        setError(fetchError instanceof Error ? fetchError.message : 'Error al cargar los datos');
        setData([]);
        setIsEmpty(true);
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    debounceRef.current = setTimeout(() => {
      void loadData();
    }, FETCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      requestIdRef.current += 1;
    };
  }, [fetchTriggerKey, isPanelActive, searchItemsKey]);

  return { data, isLoading, isEmpty, error, isPanelActive, searchItems: effectiveSearchItems };
}
